import { cleanTags, type Member } from '@engineer-os/chat';

/**
 * Where the `@names` are in a line of text.
 *
 * This is the one piece of the mention feature that both ends of the panel
 * need: the composer has to work out which tags a finished line is addressing,
 * and the list has to work out which runs of that same line to draw as
 * mentions. If those two disagreed even slightly, a learner would watch
 * themselves type a name, see it highlighted, and then find that nobody was
 * notified — or the reverse, which is worse. So there is one matcher and both
 * call it, and what the reader sees highlighted is by construction what the
 * sender addressed.
 *
 * It matches against the member list rather than against a pattern, and that
 * is the whole design. The protocol's `mentionNames` stops a run at the first
 * character a name may not hold, spaces included, which makes it useless for
 * the half of this product's names that have a space in them — a name is 2 to
 * 20 letters, digits, spaces, dots, dashes or underscores (`cleanName`), so
 * "Ada Lovelace" is one name and `@Ada Lovelace` is one mention. The only way
 * to know where that run ends is to know who is in the room. Longest name
 * first, so `@Ada Lovelace` is not read as `@Ada` followed by a surname.
 *
 * What travels is the tag, never the name. Names collide, names change, and a
 * name is a string one person can type about another; a tag is four hex
 * characters the server derived from an id nobody else has. The name is only
 * ever what was typed and what is drawn.
 */

/** One `@name` found in a line, and who it names. */
export interface MentionRun {
  /** Where the `@` is. */
  at: number;
  /** How many characters the run covers, the `@` included. */
  length: number;
  member: Member;
}

/**
 * A character a name may hold, as `cleanName` defines it minus the space.
 *
 * The space is excluded here on purpose even though a name may contain one:
 * this is used to decide whether a run *ends* where a member's name ends, and
 * a trailing space is exactly how "Ada" ends in "@Ada said". What it stops is
 * `@Ada` matching inside `@Adam`.
 */
const NAME_CHAR = /[\p{L}\p{N}._-]/u;

const isNameChar = (ch: string | undefined) => ch !== undefined && NAME_CHAR.test(ch);

/**
 * Every mention in `text`, in the order they were written.
 *
 * `members` is whoever is allowed to be matched: the whole room when the
 * composer is deciding what a message addresses, and only the people the
 * message already addresses when the list is deciding what to highlight.
 * Passing a narrower list is how the renderer avoids inventing a mention that
 * was never sent — a line that says `@ada` in a room Ada has since left is
 * text, because the tags on the wire are what decide.
 */
export function findMentions(text: string, members: readonly Member[]): MentionRun[] {
  if (members.length === 0) return [];
  // Longest first, and a stable sort, so the longest name that fits wins and
  // two people called the same thing resolve to whoever the room listed first.
  const byLength = [...members].sort((a, b) => b.name.length - a.name.length);
  const lower = text.toLowerCase();
  const runs: MentionRun[] = [];

  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '@') continue;
    // `a@b` is an address, not a mention. A run has to start a word.
    if (isNameChar(text[i - 1])) continue;
    for (const member of byLength) {
      const name = member.name.toLowerCase();
      if (name.length === 0 || !lower.startsWith(name, i + 1)) continue;
      // The run ends where the name ends: `@Ada` does not match inside `@Adam`.
      if (isNameChar(text[i + 1 + name.length])) continue;
      const length = 1 + name.length;
      runs.push({ at: i, length, member });
      // Past the run, so nothing inside a matched name is matched again.
      i += length - 1;
      break;
    }
  }

  return runs;
}

/**
 * The tags a finished line addresses, ready for the wire.
 *
 * Through the protocol's own `cleanTags` rather than a local dedupe, so the
 * cap the Worker will apply is the cap this applies — a composer that quietly
 * sent nine mentions where the room accepts eight would drop the ninth
 * silently, and which one it dropped would be the server's business rather
 * than the writer's.
 *
 * Your own tag is dropped. The room does it too (worker/room.ts), and doing it
 * here as well means the mention count in front of the writer is the count
 * that will exist.
 */
export function mentionTags(text: string, members: readonly Member[], you: Member | null): string[] {
  const tags = findMentions(text, members)
    .map((run) => run.member.tag)
    .filter((tag) => tag !== you?.tag);
  return cleanTags(tags);
}

/** A piece of a line: plain text, or a mention of somebody. */
export type TextPart = { text: string } | { run: MentionRun };

/**
 * A line cut into the runs that are mentions and the text between them.
 *
 * Returned as data rather than as elements so that the rendering — which
 * classes, which title, whether it is you — stays in the component, and so
 * this file can be reasoned about without a renderer. Nothing here produces
 * markup: the pieces are strings and a member, and the list puts each string
 * through the same text path every other message body takes.
 */
export function splitMentions(text: string, members: readonly Member[]): TextPart[] {
  const runs = findMentions(text, members);
  if (runs.length === 0) return [{ text }];
  const parts: TextPart[] = [];
  let at = 0;
  for (const run of runs) {
    if (run.at > at) parts.push({ text: text.slice(at, run.at) });
    parts.push({ run });
    at = run.at + run.length;
  }
  if (at < text.length) parts.push({ text: text.slice(at) });
  return parts;
}

/** The `@` being typed at the caret, for the composer's autocomplete. */
export interface TypedMention {
  /** Where the `@` is. */
  at: number;
  /** What has been typed after it, lower case. May be empty. */
  query: string;
}

/**
 * The mention the caret is standing in, if it is standing in one.
 *
 * Everything from the `@` to the caret, and it stops at a second space: a
 * name may hold one space, so "@ada lov" is still a name being typed, and
 * "@ada said that" is a sentence that happens to start with a mention. Two
 * words is where a list of suggestions stops being useful anyway.
 *
 * Only what is *before* the caret, because a suggestion list that reacted to
 * text after the caret would flicker while somebody edited the middle of a
 * line they had already written.
 */
export function typedMention(text: string, caret: number): TypedMention | null {
  const head = text.slice(0, caret);
  const at = head.lastIndexOf('@');
  if (at === -1) return null;
  if (isNameChar(head[at - 1])) return null;
  const query = head.slice(at + 1);
  // A newline ends it; so does a second space, and so does anything a name
  // cannot hold.
  if (/[^\p{L}\p{N}._ -]/u.test(query)) return null;
  if (query.split(' ').length > 2) return null;
  return { at, query: query.toLowerCase() };
}

/**
 * Who to offer for what has been typed so far.
 *
 * A prefix match first and a contains match after it, both case-insensitive,
 * because somebody who types `@lov` for "Ada Lovelace" means her and there is
 * nobody else it could be. You are not in your own list: mentioning yourself
 * is dropped on the way out, so offering it would be offering something that
 * does nothing.
 */
export function mentionCandidates(query: string, members: readonly Member[], you: Member | null): Member[] {
  const others = members.filter((m) => m.tag !== you?.tag);
  if (query === '') return others;
  const starts: Member[] = [];
  const holds: Member[] = [];
  for (const m of others) {
    const name = m.name.toLowerCase();
    if (name.startsWith(query)) starts.push(m);
    else if (name.includes(query)) holds.push(m);
  }
  return [...starts, ...holds];
}
